import { getLoggerInstance } from '@idmc-ms-fabric/common';
import {
	CreateItemParams,
	CreateItemResult,
	ItemLikeV2,
	NotificationType,
	TridentError,
	TridentException,
	WorkloadClientAPI,
} from '@idmc-ms-fabric/integration';
import { CoreAttr } from '@infa-hawk/models';
import { catalogSourceApi } from '@infa-service/cdgc-catalog';

import { SupportedCatalogSourceType } from './catalog-source/catalog-source';
import { GenericArtifact } from './common-types';
import { saveFabricDetailsToCS, searchCatalogFromExternalURIs } from './infa-utils';
import { getItemType } from './manifest-utils';

type CreateItemConfig<T> = {
	workspaceObjectId: string;
	itemType: string;
	displayName: string;
	description: string;
	workloadPayload: T;
	extensionClient: WorkloadClientAPI;
	externalCatalogSrcLinkFlow?: boolean;
};

export interface WorkloadPayload {
	srcArtifact: {
		id: string;
		workspaceId: string;
		type: string;
	};
	catalogSrc: {
		id: string;
		name: string;
		type: SupportedCatalogSourceType;
	};
}

export async function callArtifactCreate({
	workspaceObjectId,
	itemType,
	displayName,
	description,
	workloadPayload,
	extensionClient,
	externalCatalogSrcLinkFlow = false,
}: CreateItemConfig<WorkloadPayload>): Promise<GenericArtifact> {
	const params: CreateItemParams = {
		workspaceObjectId,
		payload: {
			itemType,
			displayName,
			description,
			workloadPayload: JSON.stringify(workloadPayload),
			payloadContentType: 'InlineJson',
		},
	};
	const logger = getLoggerInstance({});
	try {
		const {
			catalogSrc: { id: catalogSrcId, name: createdDataSourceName },
			srcArtifact: { id: selectedArtifactId, type: srcArtifactType },
		} = workloadPayload;
		const result: CreateItemResult = await extensionClient.itemCrud.createItem(params);
		logger.info('Successfully created IDQA');
		try {
			await saveFabricDetailsToCS({
				catalogSrcId,
				dqArtifactId: result.objectId,
				workspaceId: workspaceObjectId,
				artifactType: srcArtifactType,
				selectedArtifactId,
			});
		} catch (error) {
			logger.error('Content Publish Api failed, delete artifact');
			await extensionClient.itemCrud.deleteItem({ objectId: result.objectId });
			if (!externalCatalogSrcLinkFlow) {
				//! we don't wan't to delete the catalog source in case it was created externally
				logger.error('Content publish Api failed, delete data source');
				await catalogSourceApi.deleteDataSource({
					datasourceId: createdDataSourceName as string,
				});
			}
			throw error;
		}
		const response = {
			id: result.objectId,
			workspaceId: workspaceObjectId,
			type: srcArtifactType,
			displayName,
			description,
			createdBy: result.createdByUser?.name,
			createdDate: result.createdDate,
			lastModifiedBy: result.modifiedByUser?.name,
			lastModifiedDate: result.lastUpdatedDate,
		};
		return response;
	} catch (exception) {
		logger.error('Fabric Create Artifact API failed', { error: JSON.stringify(exception) });
		throw exception as TridentException;
	}
}

export async function callFetchArtifact({ objectId, extensionClient }: { objectId: string; extensionClient: WorkloadClientAPI }) {
	const logger = getLoggerInstance({});
	try {
		const fetchedArtifact = await extensionClient.itemCrud.getItem({ objectId });
		const { folderObjectId: workspaceId, itemType } = fetchedArtifact;
		/**
		 * * ExternalURI when fetching catalogSrc through objectId
		 * * fabric://<workspaceId>/Org.informatica.dataQualityArtifact/<created DqArtifactId>
		 */
		const response = await searchCatalogFromExternalURIs({ workspaceId, artifactId: objectId, artifactType: itemType });
		if (response.hits?.[0]) {
			const csId = response.hits[0]?.[CoreAttr.Origin];
			const csName = response.hits[0]?.[CoreAttr.Name];
			return {
				srcArtifact: fetchedArtifact,
				catalogSrc: {
					id: csId,
					name: csName,
					type: SupportedCatalogSourceType.warehouse,
				},
			};
		} else {
			logger.error('No Catalogue source found for this artifact.');
			throw new Error('No catalog src found for the artifact');
		}
	} catch (exception) {
		logger.error('Something went wrong while geting Artifact/Catalogue Source details', { error: exception });
		throw exception as TridentException;
	}
}

export const parseFabricException = (exception: TridentException): { message: string; code?: string; status?: number } => {
	const message = 'Unknown error please contact your administrator.';
	if (exception.error) {
		try {
			const error = JSON.parse(exception.error.message) as TridentError;

			return {
				message: error['pbi.error'].details
					.reduce((acc, msg) => {
						acc.push(msg.detail.value);
						return acc;
					}, [] as string[])
					.join(','),
				code: error.code,
				status: exception.error.status,
			};
		} catch (error) {
			console.error(error);
		}
	}

	return {
		message,
	};
};
/**
 *
 * @param param {extensionClient,item,tab}: extensionClient: WorkloadClientAPI, item: ItemLikeV2, tab: string
 * @returns {Promise<void>} void
 */
export const openSettingSidePanel = async ({
	extensionClient,
	item,
	tab,
}: {
	extensionClient: WorkloadClientAPI;
	item: Omit<ItemLikeV2, 'itemType'>;
	tab: string;
}) => {
	try {
		await extensionClient.itemSettings.open({
			item: {
				itemType: getItemType(),
				...item,
			},
			selectedSettingId: tab,
		});
	} catch (err) {
		console.error('Error opening setting side panel:', err);
		extensionClient.notification.open({
			notificationType: NotificationType.Error,
			message: 'Something went wrong while opening the setting side panel',
			title: 'Setting Error',
		});
	}
};
